/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-evaluation-model.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useRef, useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { SaveButton } from '@components/core/buttons';
import { CustomDialog, DialogContent } from '@components/core/dialog';
import Loading from '@components/core/loading';
import PageTitle from '@components/core/page-title/page-title';
import ReasonForChangeDialog from '@components/core/reason-change-dialog';
import Error from '@components/error/error';
import RagDetailTemplate from '@pages/admin/organisations/admin-evaluation-models/rag-detail-template';
import ScoringLabelTemplate from '@pages/admin/organisations/admin-evaluation-models/scoring-label-template/scoring-label-template';
import SummaryDetailTemplate from '@pages/admin/organisations/admin-evaluation-models/summary-detail-template';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import NotificationService from '@utils/notification';
import { checkIfNameExist } from '@utils/validation';
import { StudyFields } from '@queries/generated/fragments.generated';
import {
    getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel,
    getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_detectability_RiskEvaluationModelItem,
    getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_impact_RiskEvaluationModelItem,
    getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_likelihood_RiskEvaluationModelItem,
} from '@queries/generated/legacy-risk-assessment.generated';
import { GET_RISK_EVALUATION_MODELS, SAVE_RISK_EVALUATION_MODEL } from '@queries/legacy-risk-assessment';
import { GET_STUDIES } from '@queries/studies';

// makeStyles is deprecated in MUI v5, but still works until later upgrades
// It leads to errors below of the type "Property 'foo' does not exist on type 'ClassNameMap<never>'"
// (because createStyles now returns never to mark it as deprecated)
// With MUI v5, we should be using the new styled API or the sx prop instead.
// This may be required if we ever need to upgrade again, but hopefully we can avoid this
// (We only needed to upgrade in 2026.1 because React 19 required an upgrade to Mui v5)
// https://mui.com/material-ui/migration/migrating-from-jss/
const useStyles = makeStyles()(() => ({
    headerTopSticky: {
        position: 'sticky',
        top: -20,
        paddingTop: 10,
        paddingBottom: 10,
        zIndex: 100,
        backgroundColor: 'white',
        marginLeft: -1,
        marginRight: -1,
    },
    closeBtn: {
        position: 'absolute',
        top: 20,
        right: 20,
        fontSize: 26,
        cursor: 'pointer',
    },
}));

const EditEvaluationModel = ({
    evaluationModelId,
    open,
    onClose,
    data,
}: {
    evaluationModelId: string;
    open: boolean;
    onClose: Function;
    data: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel[];
}) => {
    const [loadStudies, studiesRequest] = useLazyQuery(GET_STUDIES);

    useEffect(() => {
        if (open && !studiesRequest?.data) loadStudies();
    }, [open]);

    if (studiesRequest?.loading) return <Loading float />;

    const studies = studiesRequest?.data?.organisationStudies;
    const model = data?.find(
        (m: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel) => m.id === evaluationModelId,
    );

    return <EditRiskEvaluationModel model={model} studies={studies} open={open} onClose={onClose} models={data} />;
};

const EditRiskEvaluationModel = ({
    model,
    studies,
    open,
    onClose,
    models,
}: {
    model: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel;
    studies: StudyFields[];
    open: boolean;
    onClose: Function;
    models: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel[];
}) => {
    const { classes } = useStyles();

    const defaultLabels = {
        likelihood: _.range(1, 11).map((i: number) => ({ value: i, enabled: i === 1, label: '', description: '' })),
        impact: _.range(1, 11).map((i: number) => ({ value: i, enabled: i === 1, label: '', description: '' })),
        detectability: _.range(1, 11).map((i: number) => ({ value: i, enabled: i === 1, label: '', description: '' })),
    };
    const { t } = useTranslation();

    const isEvaluationModelUsed = !!studies?.find((item: StudyFields) => item.evaluationModelId === model?.id) ?? false;

    const [saveModel, { loading: savingModel, error: saveError, data: saveData }] =
        useMutation(SAVE_RISK_EVALUATION_MODEL);
    const [name, setName] = useState(model?.name ?? '');
    const [reason, setReason] = useState(null);
    const active = useBoolean(model?.active);
    const requiredCheck = useBoolean(false);
    const disabledSaving = useBoolean(true);
    const exceededFields = useBoolean(true);
    const includeDetectability = useBoolean(model?.includeDetectability ?? true);
    const showReasonForChangeDialog = useBoolean(false);
    const showWarningLostData = useBoolean(false);
    const [labels, setLabels] = useState(
        model
            ? ({
                  likelihood: [...model.likelihood],
                  impact: [...model.impact],
                  detectability:
                      [...model.detectability] && [...model.detectability].length
                          ? [...model.detectability]
                          : defaultLabels.detectability,
              } as any)
            : defaultLabels,
    );

    const [option, setOption] = useState(Constants.RagMapOptions.MOVE);
    const ragMap = useRef(model?.ragMap ?? ([] as any));
    const [validateSectionsFields, setValidateSectionsFields] = useState([]);
    const [nameExistError, setNameExistError] = useState(null);
    const nameError = useBoolean(false);

    useEffect(() => {
        if (!open) {
            disabledSaving.setTrue();
            exceededFields.setFalse();
            requiredCheck.setFalse();
            setReason(null);
            setOption(Constants.RagMapOptions.MOVE);
            setLabels({ ...defaultLabels });
            ragMap.current = [];
            setName('');
            active.setFalse();
        }
    }, [open]);

    useEffect(() => {
        if (open && model) {
            setName(model?.name ?? '');
            active.setValue(model?.active);
            includeDetectability.setValue(model?.includeDetectability ?? true);
            const modelObj = { ...model };
            setLabels(
                model
                    ? ({
                          likelihood: [...modelObj.likelihood].map(
                              (
                                  item: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_likelihood_RiskEvaluationModelItem,
                              ) => ({
                                  ...item,
                              }),
                          ),
                          impact: [...modelObj.impact].map(
                              (
                                  item: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_impact_RiskEvaluationModelItem,
                              ) => ({ ...item }),
                          ),
                          detectability: [...modelObj.detectability]?.length
                              ? [...modelObj.detectability].map(
                                    (
                                        item: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel_detectability_RiskEvaluationModelItem,
                                    ) => ({
                                        ...item,
                                    }),
                                )
                              : defaultLabels.detectability,
                      } as any)
                    : { ...defaultLabels },
            );
        }
        ragMap.current = model?.ragMap || [];
    }, [model, open]);

    useEffect(() => {
        exceededFields.setValue(!!Object.values(validateSectionsFields).find((value: any) => value));
    }, [validateSectionsFields]);

    if (savingModel) return <Loading float />;

    if (saveError) return <Error error={saveError.message} />;

    const updateRagMap = ({ x, y, c }: any) => {
        disabledSaving.setFalse();
        const item = ragMap.current.find((i: any) => i.x === x && i.y === y);
        if (item) item.colour = c;
        else ragMap.current.push({ x, y, colour: c });
    };

    const onDisplayValuesChange = (type: string, index: number) => {
        disabledSaving.setFalse();
        const newLabels = { ...labels };
        const item = newLabels[type]?.find((d: any) => d.value === index);

        if (!item) newLabels[type].push({ value: index, enabled: true, label: '', description: '' });
        else item.enabled = !item.enabled;
        setLabels(newLabels);
    };

    const onChangeItem = (index: number, property: string, field: string, value: string) => {
        disabledSaving.setFalse();
        const newLabels = { ...labels };
        const item = newLabels[field]?.find((d: any) => d.value === index + 1);
        item[property] = value;

        setLabels(newLabels);
    };

    const saveModelFunc = (reasonValue: string | undefined) => {
        if (!reasonValue && model) {
            showReasonForChangeDialog.setTrue();
            return;
        }

        saveModel({
            variables: {
                model: {
                    id: model?.id,
                    name,
                    active: active.value,
                    includeDetectability: includeDetectability.value,
                    likelihood: labels.likelihood.map((i: any) => ({
                        description: i.description,
                        enabled: i.enabled,
                        value: i.value,
                        label: i.label,
                    })),
                    impact: labels.impact.map((i: any) => ({
                        description: i.description,
                        enabled: i.enabled,
                        value: i.value,
                        label: i.label,
                    })),
                    detectability: labels.detectability.map((i: any) => ({
                        description: i.description,
                        enabled: i.enabled,
                        value: i.value,
                        label: i.label,
                    })),
                    ragMap: ragMap.current.map((i: any) => ({ x: i.x, y: i.y, colour: i.colour })),
                },
                reason: model ? reasonValue : null,
            },
            refetchQueries: [
                {
                    query: GET_RISK_EVALUATION_MODELS,
                },
            ],
        })
            .then((result: any) => {
                disabledSaving.setTrue();
                onClose(model?.id ? 'edit' : 'add');
            })
            .catch((error) => {
                onClose('error', error);
            });
    };

    const sectionsFieldValidation = (section: any, field: any, index: any, error: any) => {
        setValidateSectionsFields({
            ...validateSectionsFields,
            [`${section}-${field}-${index}`]: error[field] !== null,
        });
    };

    const doSaveModel = () => {
        if (!name?.length) {
            requiredCheck.setTrue();
            disabledSaving.setTrue();
            return;
        }

        const nameExists = checkIfNameExist(
            {
                name,
                id: model?.id ?? null,
            },
            models,
            'name',
        );

        if (nameExists) {
            disabledSaving.setTrue();
            setNameExistError(t('admin:modelNameAlreadyExists'));
            return;
        }

        // Get all of the valid values
        const likelihoodValues = labels.likelihood.filter((l: any) => l.enabled).map((l: any) => l.value) ?? [];
        const detectabilityValues = includeDetectability.value
            ? labels.detectability.filter((l: any) => l.enabled).map((l: any) => l.value)
            : [1];
        const impactValues = labels.impact.filter((l: any) => l.enabled).map((l: any) => l.value) ?? [];

        for (let i = 0; i < likelihoodValues.length; i++)
            for (let j = 0; j < detectabilityValues.length; j++)
                for (let k = 0; k < impactValues.length; k++)
                    if (
                        !ragMap.current.find(
                            (item: any) =>
                                item.x === likelihoodValues[i] * detectabilityValues[j] && item.y === impactValues[k],
                        )
                    ) {
                        disabledSaving.setTrue();
                        NotificationService.showNotification(t('admin:modelMissingRagMapEntries'));
                        return;
                    }

        if (model && (!reason || !reason.length)) {
            showReasonForChangeDialog.setTrue();
            return;
        }

        saveModelFunc(reason);
    };

    const clearNameExistError = () => {
        setNameExistError(null);
    };

    const onModelNameError = () => {
        nameError.setTrue();
    };

    return (
        <>
            <ReasonForChangeDialog
                open={showReasonForChangeDialog.value}
                onClose={(value: string | undefined) => {
                    setReason(value);
                    showReasonForChangeDialog.setFalse();
                    if (value) saveModelFunc(value);
                }}
            />
            <WarningLostDataDialog
                open={showWarningLostData.value}
                onClose={(action: string) => {
                    if (action === 'close') onClose();
                    showWarningLostData.setFalse();
                }}
            />
            {/* Set up summary form */}
            <CustomDialog
                className="risk-details-dialog full-screen-dialog"
                onClose={onClose}
                fullWidth
                scroll="paper"
                maxWidth="lg"
                open={open}
            >
                <DialogContent style={{ position: 'relative' }}>
                    <div className={classes.headerTopSticky}>
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className={classes.closeBtn}
                            onClick={() => {
                                if (!disabledSaving.value) showWarningLostData.setTrue();
                                else {
                                    disabledSaving.setTrue();
                                    onClose();
                                }
                            }}
                            data-testid="close-dialog"
                        />
                        <PageTitle text={model ? 'Risk Evaluation Model Details' : 'Add Risk Evaluation Model'} />
                        <br />
                        <SaveButton
                            text={savingModel ? 'saving' : 'save'}
                            style={{ display: 'flex', marginLeft: 'auto' }}
                            disabled={savingModel || disabledSaving.value || exceededFields.value || nameError.value}
                            onClick={doSaveModel}
                        />
                    </div>
                    <br />
                    <div className="mb-30">
                        <SummaryDetailTemplate
                            labels={labels}
                            onDisplayValuesChange={(type: string, index: number) => {
                                onDisplayValuesChange(type, index + 1);
                            }}
                            changeDetectability={(value: boolean) => {
                                includeDetectability.setValue(value);
                                disabledSaving.setFalse();
                            }}
                            isEvaluationModelUsed={isEvaluationModelUsed}
                            changeActive={(value: boolean) => {
                                disabledSaving.setFalse();
                                active.setValue(value);
                            }}
                            modelName={name}
                            setName={(value: string) => {
                                setName(value);
                                requiredCheck.setFalse();
                                disabledSaving.setFalse();
                                nameError.setFalse();
                            }}
                            active={active.value}
                            errorNameRequired={requiredCheck.value}
                            includeDetectability={includeDetectability.value}
                            nameExistError={nameExistError}
                            clearNameExistError={clearNameExistError}
                            disableSave={onModelNameError}
                        />
                    </div>
                    {/* And the L/I/D forms */}
                    <div className="mb-30">
                        <ScoringLabelTemplate
                            items={labels.likelihood}
                            header={t('ra:likelihoodLabel')}
                            customKey="eval-likelihood"
                            onChangeItem={onChangeItem}
                            field="likelihood"
                            sectionsFieldValidation={sectionsFieldValidation}
                        />
                    </div>

                    <div className="mb-30">
                        <ScoringLabelTemplate
                            items={labels.impact}
                            header={t('ra:impact')}
                            customKey="eval-impact"
                            onChangeItem={onChangeItem}
                            field="impact"
                            sectionsFieldValidation={sectionsFieldValidation}
                        />
                    </div>

                    <div className="mb-30">
                        <ScoringLabelTemplate
                            items={labels.detectability}
                            header={t('ra:detectability')}
                            customKey="eval-detectability"
                            onChangeItem={onChangeItem}
                            field="detectability"
                            includeDetectability={includeDetectability.value}
                            sectionsFieldValidation={sectionsFieldValidation}
                        />
                    </div>

                    {/* And the RAG map */}
                    <RagDetailTemplate
                        ragMap={ragMap.current}
                        labels={labels}
                        includeDetectability={includeDetectability.value}
                        action={option}
                        onChange={updateRagMap}
                        setOption={setOption}
                        isEvaluationModelUsed={isEvaluationModelUsed}
                    />
                    <br />
                    <div className="mb-30">
                        <SaveButton
                            text={savingModel ? 'saving' : 'save'}
                            style={{ display: 'flex', marginLeft: 'auto' }}
                            disabled={savingModel || disabledSaving.value || exceededFields.value || nameError.value}
                            onClick={doSaveModel}
                        />
                    </div>
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default EditEvaluationModel;
